import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import DataTable from "react-data-table-component";
import "react-data-table-component-extensions/dist/index.css";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { customStyles } from "../../tableStyles";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faFileCsv, faFileExcel, faFilePdf, faPrint, faFileExport, faTimes, faCheck } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import '../css/AddEditOrganisation.css'
import '../css/EmailCopyControl.css'
import { orgTypeIdsForForm } from "../../utils/orgTypeHelpers";
import Select, { components } from "react-select";

const OrgControl = (props) => (
  <components.Control
    {...props}
    innerProps={{
      ...props.innerProps,
      onMouseDown: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { onControlClick } = props.selectProps;
        if (onControlClick) onControlClick();
      },
    }}
  />
);

const OrgDropdownIndicator = (props) => (
  <components.DropdownIndicator
    {...props}
    innerProps={{
      ...props.innerProps,
      onMouseDown: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { onControlClick } = props.selectProps;
        if (onControlClick) onControlClick();
      },
    }}
  />
);

const OrgSearchMenuList = (props) => {
  const { selectProps } = props;
  const inputValue = selectProps.inputValue ?? "";
  const onInputChange = selectProps.onInputChange ?? (() => {});
  return (
    <>
      <div className="org-search-in-menu">
        <input
          type="text"
          className="org-search-input"
          placeholder="Type to search"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
      <components.MenuList {...props}>{props.children}</components.MenuList>
    </>
  );
};

const formatPersonFullName = (person) => {
  const first = String(person?.name ?? "").trim();
  const last = String(person?.lname ?? "").trim();
  return [first, last].filter(Boolean).join(" ");
};

const filterPersonFullNameOption = (option, rawInput) => {
  if (!rawInput) return true;
  const needle = rawInput.trim().toLowerCase();
  return String(option?.label ?? "").toLowerCase().includes(needle);
};

const laTeamPersonMenuKey = (sectionIndex, rowIndex) =>
  `${sectionIndex}-${rowIndex}`;

const AddEditOrganizationForm = () => {
  const { id } = useParams(); // Get organization ID from URL
  const navigate = useNavigate();

  //const isEditMode = Boolean(id); // Determine if the form is in edit mode
  const [isEditMode, setIsEditMode] = useState(Boolean(id));

  // State for form data
  const [formData, setFormData] = useState({
    org_type_id: [],
    name: "",
    short_name: "",
    status: "active",
    founding_year: "",
    aff_year: "",
    website: "",
    comments: "",
    web_desc: "",
    phone1: "",
    phone2: "",
    email: "",
    email2: "",
    address1: "",
    address2: "",
    fax: "",
    country_id: "",
    city: "",
    state: "",
    zipcode: "",
    continent: "",
    active:"",
  });

  const [logoPreview, setLogoPreview] = useState("/images/logo.png");
  const [countries, setCountries] = useState([]);
  const [orgTypes, setOrgTypes] = useState([]); // Organization types
  const [activeTab, setActiveTab] = useState(isEditMode? "Members":"Profile");
  const [members, setMembers] = useState([]);
  const [pastMembers, setPastMembers] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [laTeamSections, setLaTeamSections] = useState([]);
  const [laTeamDraft, setLaTeamDraft] = useState([]);
  const [loadingLaTeam, setLoadingLaTeam] = useState(false);
  const [laTeamError, setLaTeamError] = useState("");
  const [laTeamSubTab, setLaTeamSubTab] = useState("losAngeles2028");
  const [laTeamLeadersDraft, setLaTeamLeadersDraft] = useState([]);
  const [savingTeamLeaders, setSavingTeamLeaders] = useState(false);
  const [savingLa2028, setSavingLa2028] = useState(false);
  const [persons, setPersons] = useState([]);
  const [laPersonMenuOpen, setLaPersonMenuOpen] = useState({});
  const [laPersonSearchInputs, setLaPersonSearchInputs] = useState({});
  const [dialog, setDialog] = useState({ type: "", message: "", isVisible: false });
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => 1900 + i);
  const showLosAngelesSection =
    formData.status === 1 || formData.status === "1";

  const LA_TEAM_VISIBLE_FIELDS = [
    { key: "person", label: "Name" },
    { key: "position", label: "Position" },
    { key: "email", label: "Email" },
    { key: "mobile", label: "Mobile" },
  ];

  const LA_TEAM_EXPORT_COLUMN_KEYS = [
    "First Name",
    "Last Name",
    "Organisation",
    "Position",
    "Email",
    "Mobile",
    "Team Leader Men",
    "Team Leader Women",
  ];

  const LA_TEAM_EXPORT_TITLE = "Los Angeles 2028 Team Members";

  const LA_TEAM_SECTION_KIND_ORDER = { NOC: 0, NF: 1 };

  const emptyLaTeamLeaderFields = () => ({
    team_leaderm_nf: "",
    team_leaderw_nf: "",
    team_leaderm_noc: "",
    team_leaderw_noc: "",
  });

  const normalizeLaTeamRow = (row) => ({
    _id: row?._id ?? null,
    person_id: row?.person_id ?? null,
    firstName: row?.firstName ?? "",
    lastName: row?.lastName ?? "",
    position: row?.position ?? "",
    email: row?.email ?? "",
    mobile: row?.mobile ?? "",
    team_leaderm_nf: row?.team_leaderm_nf ?? "",
    team_leaderw_nf: row?.team_leaderw_nf ?? "",
    team_leaderm_noc: row?.team_leaderm_noc ?? "",
    team_leaderw_noc: row?.team_leaderw_noc ?? "",
  });

  const padLaTeamRows = (rows) => {
    const normalized = (Array.isArray(rows) ? rows : []).map(normalizeLaTeamRow);
    while (normalized.length < 4) {
      normalized.push({
        _id: null,
        person_id: null,
        firstName: "",
        lastName: "",
        position: "",
        email: "",
        mobile: "",
        ...emptyLaTeamLeaderFields(),
      });
    }
    return normalized.slice(0, 4);
  };

  const teamLeaderFieldsForSectionKind = (kind) => {
    if (kind === "NF") {
      return { men: "team_leaderm_nf", women: "team_leaderw_nf" };
    }
    if (kind === "NOC") {
      return { men: "team_leaderm_noc", women: "team_leaderw_noc" };
    }
    return { men: null, women: null };
  };

  const formatLaTeamLeaderExportLabel = (value, kind) => {
    if (String(value ?? "").trim().toUpperCase() !== "Y") return "";
    if (kind === "NF") return "TL - NF";
    if (kind === "NOC") return "TL - NOC";
    return "";
  };

  const isLaTeamExportableRow = (row) => {
    const firstName = String(row?.firstName ?? "").trim();
    const lastName = String(row?.lastName ?? "").trim();
    const email = String(row?.email ?? "").trim();
    const position = String(row?.position ?? "").trim();
    const mobile = String(row?.mobile ?? "").trim();
    return Boolean(firstName || lastName || email || position || mobile);
  };

  const sortLaTeamSectionsForExport = (sections) =>
    [...(sections || [])].sort(
      (a, b) =>
        (LA_TEAM_SECTION_KIND_ORDER[a?.kind] ?? 2) -
        (LA_TEAM_SECTION_KIND_ORDER[b?.kind] ?? 2)
    );

  const sortLaTeamRowsForExport = (rows) =>
    [...rows].sort((a, b) => {
      const byFirst = String(a?.firstName ?? "").localeCompare(
        String(b?.firstName ?? ""),
        undefined,
        { sensitivity: "base" }
      );
      if (byFirst !== 0) return byFirst;
      return String(a?.lastName ?? "").localeCompare(
        String(b?.lastName ?? ""),
        undefined,
        { sensitivity: "base" }
      );
    });

  const formatLaTeamName = (row) =>
    `${row.firstName || ""} ${row.lastName || ""}`.trim();

  const formatLaTeamContactLabel = (row) => {
    const name = formatLaTeamName(row);
    if (name) return name;
    const email = String(row?.email ?? "").trim();
    return email || "Unnamed contact";
  };

  const getLaTeamContactOptions = (section) =>
    (section?.rows || []).filter((row) => {
      if (!row?._id) return false;
      return Boolean(formatLaTeamName(row) || String(row.email ?? "").trim());
    });

  const getCombinedLaTeamContactOptions = (sections) => {
    const seen = new Set();
    const combined = [];
    (sections || []).forEach((section) => {
      getLaTeamContactOptions(section).forEach((row) => {
        const rowId = String(row._id);
        if (seen.has(rowId)) return;
        seen.add(rowId);
        combined.push(row);
      });
    });
    return combined.sort((a, b) =>
      formatLaTeamContactLabel(a).localeCompare(formatLaTeamContactLabel(b))
    );
  };

  const resolveLaTeamContactLabel = (sections, contactId) => {
    if (!contactId) return "—";
    for (const section of sections || []) {
      const row = (section?.rows || []).find(
        (r) => r?._id != null && String(r._id) === String(contactId)
      );
      if (row) return formatLaTeamContactLabel(row);
    }
    return "—";
  };

  const isTeamLeaderSectionReadOnly = (draft) =>
    String(draft?.kind || "").trim() === "NOC";

  const isLaSectionReadOnly = (section) => section.kind === "NOC";

  const formatLaSectionTitle = (section) => section?.name || "";

  const updateLaTeamField = (sectionIndex, rowIndex, fieldKey, value) => {
    setLaTeamDraft((prev) => {
      const section = prev[sectionIndex];
      if (isLaSectionReadOnly(section)) return prev;
      return prev.map((s, sIdx) => {
        if (sIdx !== sectionIndex) return s;
        const rows = padLaTeamRows(s.rows).map((row, rIdx) => {
          if (rIdx !== rowIndex) return row;
          return { ...row, [fieldKey]: value };
        });
        return { ...s, rows };
      });
    });
  };

  const updateLaTeamPerson = (sectionIndex, rowIndex, personOrNull) => {
    setLaTeamDraft((prev) => {
      const section = prev[sectionIndex];
      if (isLaSectionReadOnly(section)) return prev;
      return prev.map((s, sIdx) => {
        if (sIdx !== sectionIndex) return s;
        const rows = padLaTeamRows(s.rows).map((row, rIdx) => {
          if (rIdx !== rowIndex) return row;
          if (!personOrNull) {
            return {
              ...row,
              person_id: null,
              firstName: "",
              lastName: "",
              email: "",
              mobile: "",
            };
          }
          return {
            ...row,
            person_id: personOrNull._id ?? null,
            firstName: personOrNull.name ?? "",
            lastName: personOrNull.lname ?? "",
            email: personOrNull.email ?? "",
            mobile: personOrNull.phone1 || personOrNull.phone2 || "",
          };
        });
        return { ...s, rows };
      });
    });
  };

  const personSelectOptions = useMemo(
    () =>
      [...persons]
        .map((person) => ({
          value: person._id,
          label: formatPersonFullName(person),
        }))
        .filter((opt) => opt.label)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
    [persons]
  );

  const resolveLaTeamPersonSelectValue = (row) => {
    if (row?.person_id) {
      const person = persons.find(
        (p) => String(p._id) === String(row.person_id)
      );
      if (person) {
        return { value: person._id, label: formatPersonFullName(person) };
      }
    }
    const fullName = formatLaTeamName(row);
    if (!fullName) return null;
    const byName = persons.find(
      (p) =>
        formatPersonFullName(p).toLowerCase() === fullName.toLowerCase()
    );
    if (byName) {
      return { value: byName._id, label: formatPersonFullName(byName) };
    }
    return { value: `__row__${row._id || fullName}`, label: fullName };
  };

  const buildLaTeamExportRows = (sections) => {
    const rows = [];
    sortLaTeamSectionsForExport(sections).forEach((section) => {
      const kind = section?.kind;
      const leaderFields = teamLeaderFieldsForSectionKind(kind);
      const exportableRows = sortLaTeamRowsForExport(
        (Array.isArray(section?.rows) ? section.rows : [])
          .map(normalizeLaTeamRow)
          .filter(isLaTeamExportableRow)
      );

      exportableRows.forEach((row) => {
        rows.push({
          "First Name": row.firstName || "",
          "Last Name": row.lastName || "",
          Organisation: section.name || "",
          Position: row.position || "",
          Email: row.email || "",
          Mobile: row.mobile || "",
          "Team Leader Men": leaderFields.men
            ? formatLaTeamLeaderExportLabel(row[leaderFields.men], kind)
            : "",
          "Team Leader Women": leaderFields.women
            ? formatLaTeamLeaderExportLabel(row[leaderFields.women], kind)
            : "",
        });
      });
    });
    return rows;
  };

  const buildLaTeamCopyText = (sections) => {
    const exportRows = buildLaTeamExportRows(sections);
    return [
      LA_TEAM_EXPORT_TITLE,
      LA_TEAM_EXPORT_COLUMN_KEYS.join("\t"),
      ...exportRows.map((row) =>
        LA_TEAM_EXPORT_COLUMN_KEYS.map((key) => row[key] ?? "").join("\t")
      ),
    ].join("\n");
  };

  const formatLaTeamRowCount = (count) =>
    `${count} row${count === 1 ? "" : "s"}`;

  // Fetch organization data, countries, members, past members, and org types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const countriesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/countries`);
        setCountries(countriesResponse.data);

        const orgTypesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/org-types`);
        setOrgTypes(orgTypesResponse.data);

        if (isEditMode) {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/organisations/${id}`);
          const organisation = response.data;
          console.log(organisation);

          setFormData({
            org_type_id: orgTypeIdsForForm(organisation.org_type_id),
            name: organisation.name || "",
            short_name: organisation.short_name || "",
            status: organisation.status || "",
            active: organisation.active || false,
            founding_year: organisation.founding_year || "",
            aff_year: organisation.aff_year || "",
            website: organisation.website || "",
            comments: organisation.comments || "",
            web_desc: organisation.web_desc || "",
            phone1: organisation.phone1 || "",
            phone2: organisation.phone2 || "",
            email: organisation.email || "",
            email2: organisation.email2 || "",
            address1: organisation.address1 || "",
            address2: organisation.address2 || "",
            fax: organisation.fax || "",
            country_id: organisation.country_id?._id || "",
            city: organisation.city || "",
            state: organisation.state || "",
            zipcode: organisation.zipcode || "",
            continent: organisation.continent || "",
          });          
     
          setLogoPreview(organisation.logo ? `/images/${organisation.logo}` : "/images/logo.png");
          fetchMembers(id);          
          setPastMembers(organisation.past_members || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const applyLaTeamResponse = (data) => {
    if (Array.isArray(data?.sections) && data.sections.length > 0) {
      setLaTeamSections(data.sections);
      setLaTeamError("");
      return;
    }

    const emptyRows = Array.from({ length: 4 }, () => ({
      _id: null,
      person_id: null,
      firstName: "",
      lastName: "",
      position: "",
      email: "",
      mobile: "",
      ...emptyLaTeamLeaderFields(),
    }));

    const sections = [];
    if (data?.nfOrganization) {
      sections.push({
        orgId: String(data.nfOrganization._id),
        name: data.nfOrganization.name || "",
        kind: "NF",
        rows: emptyRows,
      });
    }
    if (data?.nocOrganization) {
      sections.push({
        orgId: String(data.nocOrganization._id),
        name: data.nocOrganization.name || "",
        kind: "NOC",
        rows: emptyRows,
      });
    }
    setLaTeamSections(sections);
    setLaTeamError(sections.length ? "" : "No NF/NOC organisations found for this country.");
  };

  const fetchLosAngelesTeam = async (orgId) => {
    if (!orgId) return;
    setLoadingLaTeam(true);
    setLaTeamError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/organization/${orgId}/los-angeles-2028-team`
      );
      console.log("[LA2028 frontend] API response:", res.data);
      applyLaTeamResponse(res.data);
    } catch (error) {
      console.error("Error fetching Los Angeles 2028 team:", error);
      setLaTeamSections([]);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to load Los Angeles 2028 team data.";
      setLaTeamError(msg);
    } finally {
      setLoadingLaTeam(false);
    }
  };

  useEffect(() => {
    if (!showLosAngelesSection || !id) {
      setLaTeamSections([]);
      setLaTeamDraft([]);
      setLaTeamLeadersDraft([]);
      setLaTeamError("");
      return undefined;
    }
    fetchLosAngelesTeam(id);
    return undefined;
  }, [id, showLosAngelesSection]);

  useEffect(() => {
    if (!showLosAngelesSection) {
      setPersons([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/persons`
        );
        if (!cancelled) {
          setPersons(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching persons for LA team:", error);
        if (!cancelled) setPersons([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showLosAngelesSection]);

  useEffect(() => {
    if (!laTeamSections.length) {
      setLaTeamDraft([]);
      setLaTeamLeadersDraft([]);
      return;
    }
    setLaTeamDraft(
      laTeamSections.map((section) => ({
        ...section,
        rows: padLaTeamRows(section.rows).map((row) => ({ ...row })),
      }))
    );
    setLaTeamLeadersDraft(
      laTeamSections.map((section) => ({
        orgId: section.orgId,
        kind: section.kind,
        name: section.name || "",
        teamLeaderMenId: section.teamLeaderMenId
          ? String(section.teamLeaderMenId)
          : "",
        teamLeaderWomenId: section.teamLeaderWomenId
          ? String(section.teamLeaderWomenId)
          : "",
      }))
    );
  }, [laTeamSections]);

  const updateTeamLeaderSelection = (sectionIndex, field, value) => {
    setLaTeamLeadersDraft((prev) =>
      prev.map((section, idx) =>
        idx === sectionIndex ? { ...section, [field]: value } : section
      )
    );
  };

  const fetchMembers = async (orgId) => {
    setLoadingMembers(true);
    try {
      const membersRes = await axios.get(`${process.env.REACT_APP_API_URL}/organization/${orgId}/members`);
      setMembers(membersRes.data);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Toggle Xmas Card (xmas1) – PUT /persons/xmas/:id, update UI with setMembers (match by person_id or _id)
  const toggleXmas = async (personId) => {
    const id = personId != null ? String(personId) : null;
    if (!id) return;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/persons/xmas/${id}`
      );
      const newVal = res.data && (res.data.xmas1 === true || res.data.xmas1 === 1 || res.data.xmas1 === "true");
      setMembers((prev) =>
        prev.map((p) => {
          const match = (p.person_id && String(p.person_id) === id) || (p._id && String(p._id) === id);
          return match ? { ...p, xmas1: Boolean(newVal) } : p;
        })
      );
    } catch (err) {
      console.error("Error toggling Xmas card:", err);
    }
  };

  // Toggle CEO Xmas Card (xmas2) – PUT /persons/ceoxmas/:id, update UI with setMembers
  const toggleCeoXmas = async (personId) => {
    const id = personId != null ? String(personId) : null;
    if (!id) return;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/persons/ceoxmas/${id}`
      );
      const newVal = res.data && (res.data.xmas2 === true || res.data.xmas2 === 1 || res.data.xmas2 === "true");
      setMembers((prev) =>
        prev.map((p) => {
          const match = (p.person_id && String(p.person_id) === id) || (p._id && String(p._id) === id);
          return match ? { ...p, xmas2: Boolean(newVal) } : p;
        })
      );
    } catch (err) {
      console.error("Error toggling CEO Xmas card:", err);
    }
  };

  // Copy all emails where xmas1 === true; always show alert on click
  const copyXmasEmails = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const emailList = members
      .filter((person) => {
        const v = person.xmas1;
        const isTrue = v === true || v === 1 || v === "true";
        return isTrue && person.email != null && String(person.email).trim() !== "";
      })
      .map((person) => String(person.email));
    if (emailList.length > 0) {
      navigator.clipboard.writeText(emailList.join(", "));
      alert("Emails copied to clipboard!");
    } else {
      alert("No emails to copy.");
    }
  };

  // Copy all emails where xmas2 === true; always show alert on click
  const copyCeoXmasEmails = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const emailList = members
      .filter((person) => {
        const v = person.xmas2;
        const isTrue = v === true || v === 1 || v === "true";
        return isTrue && person.email != null && String(person.email).trim() !== "";
      })
      .map((person) => String(person.email));
    if (emailList.length > 0) {
      navigator.clipboard.writeText(emailList.join(", "));
      alert("Emails copied to clipboard!");
    } else {
      alert("No emails to copy.");
    }
  };

  const OLYMPIC_MEMBERSHIP_ROLE_RE = /^member_(paris_2024|la_2028)$/i;

  const isOlympicMembershipRole = (roleName) =>
    OLYMPIC_MEMBERSHIP_ROLE_RE.test(String(roleName ?? "").trim());

  const MEMBERS_COPY_COLUMNS = [
    "Name",
    "Role",
    "Email",
    "Assistant Name",
    "Assistant Email",
    "Xmas Cards",
    "CEO Xmas Cards",
  ];

  const formatMemberXmasExport = (value) => {
    const isOn = value === true || value === 1 || value === "true";
    return isOn ? "Yes" : "No";
  };

  const stripHonorific = (name) =>
    String(name ?? "")
      .trim()
      .replace(/^(Mr\.|Mrs\.|Ms\.|Miss|Dr\.|Prof\.|Sir|Lady|Dame)\s+/i, "")
      .trim();

  const formatMemberPersonName = (row) =>
    stripHonorific(row?.person_name);

  const formatMemberAssistantName = (row) => {
    const name = row?.assistant_name;
    if (name == null || name === "" || name === "--") return "No Assistant";
    return stripHonorific(name);
  };

  const formatMemberAssistantEmail = (row) => {
    const email = row?.assistant_email;
    if (email == null || email === "" || email === "N/A") return "N/A";
    return String(email).trim();
  };

  /** Same rows as Members tab — contact roles only, table-aligned field values. */
  const buildMembersExportRows = (rows) =>
    (rows || [])
      .filter((row) => !isOlympicMembershipRole(row?.role_name))
      .map((row) => ({
        Name: formatMemberPersonName(row),
        Role: row.role_name || "",
        Email: row.email || "",
        "Assistant Name": formatMemberAssistantName(row),
        "Assistant Email": formatMemberAssistantEmail(row),
        "Xmas Cards": formatMemberXmasExport(row.xmas1),
        "CEO Xmas Cards": formatMemberXmasExport(row.xmas2),
      }));

  const excelColumnLetter = (columnNumber) => {
    let n = columnNumber;
    let letter = "";
    while (n > 0) {
      const remainder = (n - 1) % 26;
      letter = String.fromCharCode(65 + remainder) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter;
  };

  /** Formatted Members Excel report — merged org heading, gap row, bold headers. */
  const exportMembersToExcel = async (orgName, memberRows) => {
    const exportRows = buildMembersExportRows(memberRows);
    const colCount = MEMBERS_COPY_COLUMNS.length;
    const lastCol = excelColumnLetter(colCount);
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Members");

    sheet.mergeCells(`A1:${lastCol}1`);
    const titleCell = sheet.getCell("A1");
    titleCell.value = String(orgName || "").trim() || "Organisation Members";
    titleCell.font = { bold: true, size: 16 };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(1).height = 30;

    sheet.getRow(2).height = 10;

    const headerRowIndex = 3;
    const headerRow = sheet.getRow(headerRowIndex);
    MEMBERS_COPY_COLUMNS.forEach((key, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = key;
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle" };
    });
    headerRow.height = 22;

    exportRows.forEach((row, rowIdx) => {
      const dataRow = sheet.getRow(headerRowIndex + 1 + rowIdx);
      MEMBERS_COPY_COLUMNS.forEach((key, colIdx) => {
        dataRow.getCell(colIdx + 1).value = row[key] ?? "";
      });
    });

    sheet.columns = [
      { width: 28 },
      { width: 24 },
      { width: 34 },
      { width: 26 },
      { width: 34 },
      { width: 14 },
      { width: 18 },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "members.xlsx");
  };

  const MEMBERS_PDF_HEADER_FILL = [0, 51, 102];

  /** Formatted Members PDF report — centred org heading, dark blue table header. */
  const exportMembersToPdf = (orgName, memberRows) => {
    const exportRows = buildMembersExportRows(memberRows);
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const title = String(orgName || "").trim() || "Organisation Members";

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(title, pageWidth / 2, 18, { align: "center" });

    doc.autoTable({
      head: [MEMBERS_COPY_COLUMNS],
      body: exportRows.map((row) =>
        MEMBERS_COPY_COLUMNS.map((key) => row[key] ?? "")
      ),
      startY: 26,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: MEMBERS_PDF_HEADER_FILL,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "left",
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250],
      },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 32 },
        2: { cellWidth: 48 },
        3: { cellWidth: 36 },
        4: { cellWidth: 48 },
        5: { cellWidth: 22 },
        6: { cellWidth: 26 },
      },
    });

    doc.save("members.pdf");
  };

  /** Tab-separated Members Copy — org name, header row, then contact roles only. */
  const buildMembersCopyText = (orgName, rows) => {
    const exportRows = buildMembersExportRows(rows);
    const dataLines = exportRows.map((row) =>
      MEMBERS_COPY_COLUMNS.map((key) => row[key] ?? "").join("\t")
    );
    const lines = [];
    if (orgName) lines.push(String(orgName).trim());
    lines.push(MEMBERS_COPY_COLUMNS.join("\t"), ...dataLines);
    return lines.join("\n");
  };

  /** Copy every non-empty assistant_email (deduped); header + bulk copy for Members table */
  const copyAssistantEmails = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const raw = members
      .map((person) => person.assistant_email)
      .filter((em) => em != null && String(em).trim() !== "")
      .map((em) => String(em).trim());
    const unique = [...new Set(raw)];
    if (unique.length > 0) {
      navigator.clipboard.writeText(unique.join(", "));
      alert("Assistant emails copied to clipboard!");
    } else {
      alert("No assistant emails to copy.");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" || type === "radio" // Handle boolean values
          ? checked
          : type === "number" // Convert numeric inputs
          ? Number(value)
          : value, // Default case for text, select, etc.
    }));
  };
   
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 48,
      height: 48,
      backgroundColor: "#fff",
      border: "1px solid #ced4da",
      borderRadius: 6,
      boxShadow: "none",
      outline: "none",
      cursor: "text",
      ":hover": {
        borderColor: "#ced4da",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: 48,
      padding: "0 1rem",
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      overflowX: "auto",
      overflowY: "hidden",
      gap: 8,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: "#212529",
      fontSize: "1rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
      fontSize: "1rem",
      whiteSpace: "nowrap",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
      fontSize: "1rem",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "transparent",
      border: "none",
      margin: 0,
      flex: "0 0 auto",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#212529",
      fontSize: "1rem",
      padding: 0,
      paddingRight: 6,
      whiteSpace: "nowrap",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      borderRadius: 6,
      padding: "0 6px",
      ":hover": {
        backgroundColor: "rgba(0,0,0,0.06)",
        color: "#212529",
      },
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: 48,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      border: "1px solid #ced4da",
      borderRadius: 6,
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      overflow: "hidden",
    }),
    menuList: (provided) => ({
      ...provided,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "1rem",
      color: "#212529",
      backgroundColor: state.isSelected
        ? "rgba(13,110,253,0.12)"
        : state.isFocused
        ? "rgba(0,0,0,0.04)"
        : "#fff",
      ":active": {
        backgroundColor: "rgba(0,0,0,0.06)",
      },
    }),
  };
  /** Address tab: copy Email 1 / Email 2 to clipboard (same UX as Members table) */
  const copyAddressEmail = (value) => {
    if (value == null || String(value).trim() === "") return;
    navigator.clipboard.writeText(String(value));
    alert("Email copied to clipboard!");
  };

  // Handle logo file selection
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleEditorChange = (content) => {
    setFormData((prev) => ({
      
      ...prev,
      web_desc: content, // Bind editor content to web_desc
    }));
  };

  const handleCloseDialog = () => {
    setDialog({ type: "", message: "", isVisible: false });
  };

  const handleSaveOrganization = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      // Create a FormData object
      const uploadFormData = new FormData();
  
      // Append all form fields (org types: multiple keys so Express parses an array)
      for (const [key, value] of Object.entries(formData)) {
        if (key === "org_type_id") continue;
        if (value === null || value === undefined) continue;
        uploadFormData.append(key, value);
      }
      if (Array.isArray(formData.org_type_id)) {
        formData.org_type_id.forEach((typeId) => {
          if (typeId) uploadFormData.append("org_type_id", typeId);
        });
      }
  
      // Append the file with the key "logo" as expected by the backend route
      if (selectedFile) {
        uploadFormData.append("logo", selectedFile);
      }
  
      let response;
  
      if (isEditMode) {
        // Update the organization using PUT method
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/organisations/${id}`,
          uploadFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        // Create a new organization using POST method
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/organizations`,
          uploadFormData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
  
        // Extract the new organization data
        const newOrganization = response.data.organization;
        setFormData(newOrganization); // Update formData state with the new organization data
        setIsEditMode(true);
        setDialog({ type: "success", message: "Organization added successfully!", isVisible: true });
  
        // Update the URL to include the new organization's ID (optional)
        window.history.pushState({}, "", `/organisations/${newOrganization._id}`);
      }
  
      // Show success dialog
      setDialog({ type: "success", message: "Organization saved successfully!", isVisible: true });
    } catch (error) {
      console.error("Error saving organization:", error);
      setDialog({ type: "failed", message: "Failed to save organization. Please try again.", isVisible: true });
    }
  };

  const handleSubmit = handleSaveOrganization;

  const handleSaveLa2028 = async () => {
    if (!id) return;
    setSavingLa2028(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/organization/${id}/los-angeles-2028-team`,
        {
          sections: laTeamDraft
            .filter((section) => !isLaSectionReadOnly(section))
            .map((section) => ({
              orgId: section.orgId,
              kind: section.kind,
              rows: padLaTeamRows(section.rows).map((row) =>
                normalizeLaTeamRow(row)
              ),
            })),
        }
      );
      applyLaTeamResponse(res.data);
      setDialog({
        type: "success",
        message: "Los Angeles 2028 planning data saved.",
        isVisible: true,
      });
    } catch (error) {
      console.error("Error saving Los Angeles 2028 planning data:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to save Los Angeles 2028 planning data.";
      setDialog({ type: "failed", message: msg, isVisible: true });
    } finally {
      setSavingLa2028(false);
    }
  };

  const handleSaveTeamLeaders = async () => {
    if (!id) return;
    setSavingTeamLeaders(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/organization/${id}/team-leaders`,
        {
          sections: laTeamLeadersDraft
            .filter((section) => !isTeamLeaderSectionReadOnly(section))
            .map((section) => ({
              orgId: section.orgId,
              kind: section.kind,
              teamLeaderMenId: section.teamLeaderMenId || null,
              teamLeaderWomenId: section.teamLeaderWomenId || null,
            })),
        }
      );
      applyLaTeamResponse(res.data);
      setDialog({
        type: "success",
        message: "Team leaders saved successfully.",
        isVisible: true,
      });
    } catch (error) {
      console.error("Error saving team leaders:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to save team leaders.";
      setDialog({ type: "failed", message: msg, isVisible: true });
    } finally {
      setSavingTeamLeaders(false);
    }
  };

  // Render Profile Tab
  const renderProfileTab = () => (
    <div>
      <div className="row mt-3 g-3">
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Organization Type</label>
          <Select
            isMulti
            name="org_type_id"
            className="crm-editorg-react-select"
            classNamePrefix="crm-editorg-react-select"
            styles={customSelectStyles}
            options={orgTypes.map((type) => ({
            value: type._id, label: type.org_type,
          }))}
         value={orgTypes
        .filter((type) => formData.org_type_id.includes(type._id))
       .map((type) => ({
        value: type._id,
        label: type.org_type,
        }))
        }
  onChange={(selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    setFormData((prev) => ({
      ...prev,
      org_type_id: values,
    }));
  }}
/>
         
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Name</label>
          <input type="text" className="form-control py-3" name="name" value={formData.name} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Short Name</label>
          <input type="text" className="form-control py-3" name="short_name" value={formData.short_name} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Status</label>
          <div>
            <label className="me-2">
              <input type="radio" name="status" value="1" checked={formData.status === 1} onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            status : 1, // Convert string to boolean
          }))
        } />
              Active
            </label>
            <label>
              <input type="radio" name="status" value="0" checked={formData.status === 0} onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            status : 0, // Convert string to boolean
          }))
        } />
              Inactive
            </label>
          </div>
        </div>
      </div>
      <div className="row mt-3 g-3">
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Year Founded</label>       
        <select
          id="foundingYearDropdown"
          name="founding_year"
          value={formData.founding_year}
          onChange={handleInputChange}
          className="form-control py-3 crm-editorg-select"
        >
          <option value="">-- Select Founding Year --</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Year IGF Affiliation</label>
          <select
          id="affYearDropdown"
          name="aff_year"
          value={formData.aff_year}
          onChange={handleInputChange}
          className="form-control py-3 crm-editorg-select"
        >
          <option value="">-- Select Affiliation Year --</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Website</label>
          <input type="text" className="form-control py-3" name="website" value={formData.website} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Comments</label>
          <textarea className="form-control py-3" name="comments" value={formData.comments} onChange={handleInputChange} />
        </div>
      </div>
      <div className="row mt-3 g-3">
        <div className="form-group col-12">
          <label>Website Description</label>
          <Editor
            apiKey="zk2zzdzsc5ofia3cqt9boi8numoj5fr2y4lj21p6lstv9t9t"
            value={formData.web_desc}
            init={{
              height: 500,
              menubar: false,
              plugins: [
                "advlist autolink lists link image charmap print preview anchor",
                "searchreplace visualblocks code fullscreen",
                "insertdatetime media table paste code help wordcount",
              ],
              toolbar:
                "undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
            }}
            onEditorChange={handleEditorChange}
          />       
        </div>
      </div>
    </div>
  );

  // Render Address Tab
  const renderAddressTab = () => (
    <div>
      <div className="row mt-3 g-3">
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Landline</label>
          <input type="text" className="form-control py-3" name="phone1" value={formData.phone1} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Mobile Phone</label>
          <input type="text" className="form-control py-3" name="phone2" value={formData.phone2} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Email 1</label>
          <div className="email-copy-wrapper">
            <input
              type="email"
              className="form-control py-3"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <button
              type="button"
              className="email-copy-btn"
              onClick={() => copyAddressEmail(formData.email)}
              title="Copy Email 1"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
            </button>
          </div>
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Email 2</label>
          <div className="email-copy-wrapper">
            <input
              type="email"
              className="form-control py-3"
              name="email2"
              value={formData.email2}
              onChange={handleInputChange}
            />
            <button
              type="button"
              className="email-copy-btn"
              onClick={() => copyAddressEmail(formData.email2)}
              title="Copy Email 2"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
            </button>
          </div>
        </div>
      </div>

      <div className="row mt-3 g-3">
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Address Line 1</label>
          <input type="text" className="form-control py-3" name="address1" value={formData.address1} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>City</label>
          <input type="text" className="form-control py-3" name="city" value={formData.city} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>State</label>
          <input type="text" className="form-control py-3" name="state" value={formData.state} onChange={handleInputChange} />
        </div>
        <div className="form-group col-12 col-md-6 col-xl-3">
          <label>Country</label>
          <select className="form-control py-3 crm-editorg-select" name="country_id" value={formData.country_id} onChange={handleInputChange}>
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  // Render Members Tab  

  const renderMembersTab = () => {


    const exportToExcel = async () => {
      try {
        await exportMembersToExcel(formData.name || "", members);
        setDialog({ type: "members", message: "Excel Generated!", isVisible: true });
      } catch (err) {
        console.error("Members Excel export:", err);
        setDialog({
          type: "failed",
          message: "Failed to generate Excel file.",
          isVisible: true,
        });
      }
    };
  
    const exportToPdf = () => {
      try {
        exportMembersToPdf(formData.name || "", members);
        setDialog({ type: "members", message: "PDF Generated!", isVisible: true });
      } catch (err) {
        console.error("Members PDF export:", err);
        setDialog({
          type: "failed",
          message: "Failed to generate PDF file.",
          isVisible: true,
        });
      }
    };
  
    const copyTableToClipboard = () => {
      const textData = buildMembersCopyText(formData.name || "", members);
      navigator.clipboard.writeText(textData);
      setDialog({ type: "members", message: "Copied to clipboard!", isVisible: true });      
    };

    const exportToCsv = () => {
      const exportRows = buildMembersExportRows(members);
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "members.csv");
      setDialog({ type: "members", message: "Csv Generated!", isVisible: true }); 
    };
  
    const printTable = () => {
      const exportRows = buildMembersExportRows(members);
      const printWindow = window.open("", "", "width=800,height=600");
      if (!printWindow) {
        alert("Unable to open print window. Please allow pop-ups for this site and try again.");
        return;
      }
      const headerCells = MEMBERS_COPY_COLUMNS.map((key) => `<th>${key}</th>`).join("");
      const printContent = `
        <table border="1" style="width:100%;border-collapse:collapse;">
          <thead>
            <tr>${headerCells}</tr>
          </thead>
          <tbody>
            ${exportRows
              .map(
                (row) =>
                  `<tr>${MEMBERS_COPY_COLUMNS.map(
                    (key) => `<td>${row[key] ?? ""}</td>`
                  ).join("")}</tr>`
              )
              .join("")}
          </tbody>
        </table>
      `;
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    };
  
    const handleRowClick = (row) => {
      navigate(`/edit-person/${row.person_id}`, { state: { person: row } }); // Use _id
    };

    /** Assistant columns must not bubble to DataTable row click (would open main member e.g. Peter). */
    const goToAssistantPerson = (e, row) => {
      e.preventDefault();
      e.stopPropagation();
      const aid = row.assistant_id;
      if (aid == null || aid === "") return;
      navigate(`/edit-person/${String(aid)}`);
    };

    const exportFull = async () => {
      exportToCsv();
      await exportToExcel();
      exportToPdf();
      setDialog({ type: "members", message: "Full export (CSV, Excel, PDF) completed!", isVisible: true });
    };

    // Columns for DataTable
    const columns = [
      {
        name: "#",
        cell: (row, index) => index + 1,
        width: "60px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
     
      {
        name: 'Name',
        selector: (row) => row.person_name,
        sortable: true,
        ignoreRowClick: true,
        cell: (row) => (
          <span
            style={{ color: "#000", cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRowClick(row);
            }}
          >
            {row.person_name}
          </span>
        ),
      },
      { name: "Role", selector: (row) => row.role_name, sortable: true, ignoreRowClick: true },
      {
        name: "Email",
        width: "280px",
        grow: 1,
        selector: (row) => row.email ?? "",
        sortable: true,
        ignoreRowClick: true,
        cell: (row) => (
          <div className="email-copy-cell" onClick={(e) => e.stopPropagation()}>
            <span className="email-text">{row.email != null ? String(row.email) : ""}</span>
            <button
              type="button"
              className="email-copy-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const email = row.email != null && row.email !== "" ? String(row.email) : null;
                if (email) {
                  navigator.clipboard.writeText(email);
                  alert("Email copied to clipboard!");
                }
              }}
              title="Copy Email"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
            </button>
          </div>
        ),
      },
      {
        id: "assistant_name",
        name: "Assistant Name",
        width: "200px",
        selector: (row) => row.assistant_name || "No Assistant",
        sortable: true,
        ignoreRowClick: true,
        cell: (row) => {
          const assistantName =
            row.assistant_name != null && row.assistant_name !== ""
              ? String(row.assistant_name)
              : null;
          if (!assistantName) return "No Assistant";

          const canOpenAssistant =
            row.assistant_id != null && row.assistant_id !== "";

          return (
            <div
              className="crm-editorg-assistant-name-cell"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span
                className={canOpenAssistant ? "crm-editorg-assistant-name-link" : undefined}
                role={canOpenAssistant ? "button" : undefined}
                tabIndex={canOpenAssistant ? 0 : undefined}
                onClick={
                  canOpenAssistant
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        goToAssistantPerson(e, row);
                      }
                    : undefined
                }
                onKeyDown={
                  canOpenAssistant
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          goToAssistantPerson(e, row);
                        }
                      }
                    : undefined
                }
              >
                {assistantName}
              </span>
            </div>
          );
        },
      },

      {
        id: "assistant_email",
        width: "240px",
        name: (
          <div
            className="crm-editorg-col-header-copy"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Assistant Email</span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                copyAssistantEmails(e);
              }}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
              }}
              title="Copy all assistant emails"
            >
              <FontAwesomeIcon icon={faCopy} style={{ color: "#fff" }} />
            </button>
          </div>
        ),
        selector: (row) =>
          row.assistant_email != null && row.assistant_email !== ""
            ? String(row.assistant_email)
            : "N/A",
        sortable: true,
        ignoreRowClick: true,
        button: true,
        cell: (row) => {
          const assistantEmail =
            row.assistant_email != null && row.assistant_email !== ""
              ? String(row.assistant_email).trim()
              : null;
          if (!assistantEmail) return "N/A";
          return (
            <div
              className="email-copy-cell"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span className="email-text">{assistantEmail}</span>
              <button
                type="button"
                className="email-copy-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigator.clipboard.writeText(assistantEmail);
                  alert("Email copied to clipboard!");
                }}
                title="Copy assistant email"
              >
                <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
              </button>
            </div>
          );
        },
      },
     
     
         {
       name: (
         <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
           <span>Xmas Cards</span>
           <button
             type="button"
             onClick={(e) => {
               e.preventDefault();
               e.stopPropagation();
               copyXmasEmails(e);
             }}
             style={{
               border: "none",
               background: "none",
               cursor: "pointer",
             }}
             title="Copy Xmas Emails"
           >
             <FontAwesomeIcon icon={faCopy} style={{ color: "#fff" }} />
           </button>
         </div>
       ),
       width: '120px',
       center: true,
       button: true,
       sortable: false,
       ignoreRowClick: true,
       cell: (row) => {
         const v = row && row.xmas1;
         const isXmas1 = v === true || v === 1 || v === "true";
         const personId = row.person_id != null ? row.person_id : row._id;
         return (
           <span onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}>
             {isXmas1 ? (
               <FontAwesomeIcon icon={faCheck} style={{ color: "green" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (personId) toggleXmas(personId); }} />
             ) : (
               <FontAwesomeIcon icon={faTimes} style={{ color: "black" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (personId) toggleXmas(personId); }} />
             )}
           </span>
         );
       },
     },
     {
       name: (
         <div style={{
           display: "flex",
           alignItems: "center",
           gap: "6px",
           justifyContent: "center",
           whiteSpace: "nowrap",
         }} onClick={(e) => e.stopPropagation()}>
           <span>CEO Xmas Cards</span>
           <button
             type="button"
             onClick={(e) => {
               e.preventDefault();
               e.stopPropagation();
               copyCeoXmasEmails(e);
             }}
             style={{
               border: "none",
               background: "none",
               cursor: "pointer",
             }}
             title="Copy CEO Xmas Emails"
           >
             <FontAwesomeIcon icon={faCopy} style={{ color: "#fff" }} />
           </button>
         </div>
       ),
       width: "140px",
       center: true,
       button: true,
       sortable: false,
       ignoreRowClick: true,
       cell: (row) => {
         const v = row && row.xmas2;
         const isXmas2 = v === true || v === 1 || v === "true";
         const personId = row.person_id != null ? row.person_id : row._id;
         return (
           <span onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }}>
             {isXmas2 ? (
               <FontAwesomeIcon icon={faCheck} style={{ color: "green" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (personId) toggleCeoXmas(personId); }} />
             ) : (
               <FontAwesomeIcon icon={faTimes} style={{ color: "black" }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (personId) toggleCeoXmas(personId); }} />
             )}
           </span>
         );
       },
     }, 
    ];

    const data = {
      columns,
      data: members,
    };
  
    const renderMembersActionRow = () => (
      <div className="crm-editorg-members-section__actions mt-3">
        <div className="crm-editorg-members-section__nav d-flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-success text-white px-4 py-3 crm-editorg-members-nav-btn"
            onClick={() => navigate("/add-person")}
          >
            <span className="d-block">Add New Individual</span>
            <span className="d-block">&amp; Assign Role</span>
          </button>
          <button
            type="button"
            className="btn crm-editorg-members-nav-btn crm-editorg-members-nav-btn--gold"
            onClick={() => navigate("/individuals")}
          >
            <span className="d-block">Find Existing Individual</span>
            <span className="d-block">&amp; Assign Role</span>
          </button>
        </div>
        <button
          type="button"
          className="btn btn-success px-5 py-3 crm-editorg-members-section__save"
          style={{ fontWeight: "600", minWidth: "150px" }}
          onClick={handleSaveOrganization}
        >
          Save
        </button>
      </div>
    );

    return (
      <div className="crm-editorg-members-content">
        <div className="crm-editorg-members-section">
          {loadingMembers ? (
            <div>Loading members...</div>
          ) : members.length > 0 ? (
            <>
              <div className="mb-3">
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={() => copyTableToClipboard()}>
                  <FontAwesomeIcon icon={faCopy} className="me-2" />
                  Copy
                </button>
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={exportToCsv}>
                  <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                  CSV
                </button>
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={exportToExcel}>
                  <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                  Excel
                </button>
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={exportToPdf}>
                  <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                  PDF
                </button>
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={printTable}>
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                  Print
                </button>
                <button type="button" className="btn btn-success px-5 py-3 mx-2" onClick={exportFull}>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  Full Export
                </button>
              </div>
              <div className="crm-editorg-table-wrapper">
                <DataTable
                  columns={columns}
                  data={members}
                  pagination
                  paginationPerPage={50}
                  paginationComponentOptions={{ noRowsPerPage: true }}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                />
              </div>
            </>
          ) : (
            <div>No members found for this organization.</div>
          )}
          {renderMembersActionRow()}
        </div>
      </div>
    );
  };

  const renderLaTeamFieldInput = (
    sectionIndex,
    rowIndex,
    fieldKey,
    row,
    readOnly,
    section
  ) => {
    const inputClass = `form-control crm-editorg-la-team__input${
      readOnly ? " crm-editorg-la-team__input--readonly" : ""
    }`;

    if (fieldKey === "person") {
      const menuKey = laTeamPersonMenuKey(sectionIndex, rowIndex);
      const displayName = formatLaTeamName(row);

      if (readOnly) {
        return (
          <div className="crm-editorg-la-team__field">
            <input
              type="text"
              className={`${inputClass} crm-editorg-la-team__input--readonly`}
              readOnly
              value={displayName}
              tabIndex={-1}
            />
          </div>
        );
      }

      return (
        <div className="crm-editorg-la-team__field">
          <Select
            classNamePrefix="react-select"
            className="crm-editorg-la-team__select"
            placeholder="Select name"
            isSearchable
            options={personSelectOptions}
            value={resolveLaTeamPersonSelectValue(row)}
            onChange={(opt) => {
              if (!opt) {
                updateLaTeamPerson(sectionIndex, rowIndex, null);
                return;
              }
              const person = persons.find(
                (p) => String(p._id) === String(opt.value)
              );
              if (person) updateLaTeamPerson(sectionIndex, rowIndex, person);
            }}
            isClearable
            filterOption={filterPersonFullNameOption}
            menuIsOpen={!!laPersonMenuOpen[menuKey]}
            onMenuOpen={() =>
              setLaPersonMenuOpen((prev) => ({ ...prev, [menuKey]: true }))
            }
            onMenuClose={() => {
              setLaPersonMenuOpen((prev) => ({ ...prev, [menuKey]: false }));
              setLaPersonSearchInputs((prev) => ({ ...prev, [menuKey]: "" }));
            }}
            onControlClick={() =>
              setLaPersonMenuOpen((prev) => ({
                ...prev,
                [menuKey]: !prev[menuKey],
              }))
            }
            inputValue={laPersonSearchInputs[menuKey] ?? ""}
            onInputChange={(value) =>
              setLaPersonSearchInputs((prev) => ({
                ...prev,
                [menuKey]: value,
              }))
            }
            components={{
              Input: () => null,
              Control: OrgControl,
              DropdownIndicator: OrgDropdownIndicator,
              MenuList: OrgSearchMenuList,
            }}
          />
        </div>
      );
    }

    if (fieldKey === "email" || fieldKey === "mobile") {
      const value = String(row[fieldKey] ?? "").trim();
      const readonlyClass = `form-control crm-editorg-la-team__input crm-editorg-la-team__input--readonly`;

      if (fieldKey === "email") {
        return (
          <div className="crm-editorg-la-team__field">
            <div className="email-copy-wrapper">
              <input
                type="text"
                className={readonlyClass}
                readOnly
                value={row.email ?? ""}
                tabIndex={-1}
              />
              {value ? (
                <button
                  type="button"
                  className="email-copy-btn"
                  onClick={() => navigator.clipboard.writeText(value)}
                  title="Copy email"
                >
                  <FontAwesomeIcon icon={faCopy} style={{ color: "#D9A128" }} />
                </button>
              ) : null}
            </div>
          </div>
        );
      }

      return (
        <div className="crm-editorg-la-team__field">
          <input
            type="text"
            className={readonlyClass}
            readOnly
            value={row.mobile ?? ""}
            tabIndex={-1}
          />
        </div>
      );
    }

    const value = String(row[fieldKey] ?? "");

    return (
      <div className="crm-editorg-la-team__field">
        <input
          type="text"
          className={inputClass}
          readOnly={readOnly}
          value={value}
          onChange={(e) =>
            updateLaTeamField(sectionIndex, rowIndex, fieldKey, e.target.value)
          }
        />
      </div>
    );
  };

  const renderTeamLeaderField = (
    sectionKey,
    fieldKey,
    label,
    draft,
    sectionIndex,
    readOnly,
    contactOptions
  ) => {
    const inputId = `${sectionKey}-${fieldKey}`;
    const value = draft[fieldKey] || "";

    return (
      <div className="crm-editorg-la-team-leaders__field">
        <label
          className="crm-editorg-la-team-leaders__label"
          htmlFor={readOnly ? undefined : inputId}
        >
          {label}
        </label>
        {readOnly ? (
          <div
            className="crm-editorg-la-team-leaders__value"
            id={inputId}
            aria-readonly="true"
          >
            {resolveLaTeamContactLabel(laTeamSections, value)}
          </div>
        ) : (
          <select
            id={inputId}
            className="form-control crm-editorg-select crm-editorg-la-team-leaders__select"
            value={value}
            onChange={(e) =>
              updateTeamLeaderSelection(sectionIndex, fieldKey, e.target.value)
            }
          >
            <option value="">— Select —</option>
            {contactOptions.map((row) => (
              <option key={String(row._id)} value={String(row._id)}>
                {formatLaTeamContactLabel(row)}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  };

  const renderTeamLeaderBlock = (draft, section, sectionIndex) => {
    const sectionKey =
      draft.orgId != null && String(draft.orgId).trim() !== ""
        ? String(draft.orgId)
        : `team-leader-section-${sectionIndex}`;
    const readOnly = isTeamLeaderSectionReadOnly(draft);
    const contactOptions = readOnly
      ? []
      : getCombinedLaTeamContactOptions(laTeamSections);

    return (
      <div
        key={sectionKey}
        className={`crm-editorg-la-team-leaders__block${
          readOnly ? " crm-editorg-la-team-leaders__block--readonly" : ""
        }`}
      >
        <h3 className="crm-editorg-la-team-leaders__org-heading">
          {draft.name || ""}
        </h3>
        <div className="crm-editorg-la-team-leaders__fields">
          {renderTeamLeaderField(
            sectionKey,
            "teamLeaderMenId",
            "Team Leader Men",
            draft,
            sectionIndex,
            readOnly,
            contactOptions
          )}
          {renderTeamLeaderField(
            sectionKey,
            "teamLeaderWomenId",
            "Team Leader Women",
            draft,
            sectionIndex,
            readOnly,
            contactOptions
          )}
        </div>
      </div>
    );
  };

  const renderLaTeamSectionGrid = (section, sectionIndex) => {
    const sectionKey =
      section.orgId != null && String(section.orgId).trim() !== ""
        ? String(section.orgId)
        : `section-${sectionIndex}`;
    const rows = padLaTeamRows(section.rows);
    const readOnly = isLaSectionReadOnly(section);

    return (
      <div key={sectionKey} className="crm-editorg-la-team__block">
        <h3 className="crm-editorg-la-team__org-name">
          {formatLaSectionTitle(section)}
        </h3>
        <div className="crm-editorg-la-team__grid-wrap">
          <div className="crm-editorg-la-team__grid-head">
            {LA_TEAM_VISIBLE_FIELDS.map((f) => (
              <div key={f.key} className="crm-editorg-la-team__col-label">
                {f.label}
              </div>
            ))}
          </div>
          {rows.map((row, rowIndex) => (
            <div
              key={row._id || `${sectionKey}-row-${rowIndex}`}
              className="crm-editorg-la-team__grid-row"
            >
              {LA_TEAM_VISIBLE_FIELDS.map((f) => (
                <React.Fragment key={f.key}>
                  {renderLaTeamFieldInput(
                    sectionIndex,
                    rowIndex,
                    f.key,
                    row,
                    readOnly,
                    section
                  )}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLosAngelesSection = () => {
    const laToolbarBtn = "btn btn-success px-5 py-3 mx-2";

    const copyLaTeamToClipboard = async () => {
      try {
        const exportRows = buildLaTeamExportRows(laTeamDraft);
        await navigator.clipboard.writeText(buildLaTeamCopyText(laTeamDraft));
        alert(`Copied ${formatLaTeamRowCount(exportRows.length)} to clipboard.`);
      } catch (error) {
        console.error("LA 2028 copy failed:", error);
      }
    };

    const exportLaTeamCsv = () => {
      try {
        const exportRows = buildLaTeamExportRows(laTeamDraft);
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "los_angeles_2028_team_leaders.csv");
        alert(`CSV downloaded. Added ${formatLaTeamRowCount(exportRows.length)} to CSV.`);
      } catch (error) {
        console.error("LA 2028 CSV export failed:", error);
      }
    };

    const exportLaTeamExcel = () => {
      try {
        const exportRows = buildLaTeamExportRows(laTeamDraft);
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "LA 2028 Team Leaders");
        XLSX.writeFile(wb, "los_angeles_2028_team_leaders.xlsx");
        alert(`Excel downloaded. Added ${formatLaTeamRowCount(exportRows.length)} to Excel.`);
      } catch (error) {
        console.error("LA 2028 Excel export failed:", error);
      }
    };

    const exportLaTeamPdf = () => {
      try {
        const exportRows = buildLaTeamExportRows(laTeamDraft);
        const doc = new jsPDF();
        doc.autoTable({
          head: [LA_TEAM_EXPORT_COLUMN_KEYS],
          body: exportRows.map((r) =>
            LA_TEAM_EXPORT_COLUMN_KEYS.map((key) => r[key] ?? "")
          ),
        });
        doc.save("los_angeles_2028_team_leaders.pdf");
        alert(`PDF downloaded. Added ${formatLaTeamRowCount(exportRows.length)} to PDF.`);
      } catch (error) {
        console.error("LA 2028 PDF export failed:", error);
      }
    };

    const printLaTeam = () => {
      const exportRows = buildLaTeamExportRows(laTeamDraft);
      const printWindow = window.open("", "", "width=800,height=600");
      if (!printWindow) {
        alert("Unable to open print window. Please allow pop-ups for this site and try again.");
        return;
      }
      const headerCells = LA_TEAM_EXPORT_COLUMN_KEYS.map(
        (key) => `<th>${key}</th>`
      ).join("");
      const tableHtml = `
        <table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;">
          <thead>
            <tr>${headerCells}</tr>
          </thead>
          <tbody>
            ${exportRows
              .map(
                (r) =>
                  `<tr>${LA_TEAM_EXPORT_COLUMN_KEYS.map(
                    (key) => `<td>${r[key] ?? ""}</td>`
                  ).join("")}</tr>`
              )
              .join("")}
          </tbody>
        </table>`;
      printWindow.document.write(`<html><body>${tableHtml}</body></html>`);
      printWindow.document.close();
      printWindow.print();
    };

    if (loadingLaTeam) {
      return (
        <div className="crm-editorg-la-team text-center text-muted py-4">
          Loading Los Angeles 2028 team…
        </div>
      );
    }

    return (
      <div className="crm-editorg-la-team">
        <div className="crm-editorg-la-team__subtabs d-flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            className={`btn px-4 py-3 ${
              laTeamSubTab === "losAngeles2028" ? "btn-warning" : "btn-success"
            }`}
            onClick={() => setLaTeamSubTab("losAngeles2028")}
          >
            Los Angeles 2028
          </button>
          <button
            type="button"
            className={`btn px-4 py-3 ${
              laTeamSubTab === "teamLeaders" ? "btn-warning" : "btn-success"
            }`}
            onClick={() => setLaTeamSubTab("teamLeaders")}
          >
            Team Leaders
          </button>
        </div>

        {laTeamSubTab === "losAngeles2028" ? (
          <>
            <div className="crm-editorg-la-team__toolbar">
              <button type="button" className={laToolbarBtn} onClick={copyLaTeamToClipboard}>
                <FontAwesomeIcon icon={faCopy} className="me-2" />
                Copy
              </button>
              <button type="button" className={laToolbarBtn} onClick={exportLaTeamCsv}>
                <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                CSV
              </button>
              <button type="button" className={laToolbarBtn} onClick={exportLaTeamExcel}>
                <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                Excel
              </button>
              <button type="button" className={laToolbarBtn} onClick={exportLaTeamPdf}>
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                PDF
              </button>
              <button type="button" className={laToolbarBtn} onClick={printLaTeam}>
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </button>
            </div>

            {!laTeamDraft.length ? (
              <p className="crm-editorg-la-team__empty text-center text-muted">
                {laTeamError ||
                  "No Los Angeles 2028 team data for this organisation."}
              </p>
            ) : (
              <>
                {laTeamDraft.map((section, sectionIndex) =>
                  renderLaTeamSectionGrid(section, sectionIndex)
                )}
                <div className="crm-editorg-la-section__save mt-4">
                  <button
                    type="button"
                    className="btn btn-success px-5 py-3"
                    style={{ fontWeight: "600", minWidth: "150px" }}
                    onClick={handleSaveLa2028}
                    disabled={savingLa2028}
                  >
                    {savingLa2028 ? "Saving…" : "Save"}
                  </button>
                </div>
              </>
            )}
          </>
        ) : !laTeamLeadersDraft.length ? (
          <p className="crm-editorg-la-team__empty text-center text-muted">
            {laTeamError ||
              "No team leader data for this organisation."}
          </p>
        ) : (
          <div className="crm-editorg-la-team-leaders">
            {laTeamLeadersDraft.map((draft, sectionIndex) => {
              const section =
                laTeamSections.find(
                  (s) => String(s.orgId) === String(draft.orgId)
                ) || { rows: [] };
              return renderTeamLeaderBlock(draft, section, sectionIndex);
            })}
            <div className="crm-editorg-la-team-leaders__save">
              <button
                type="button"
                className="crm-editorg-la-team-leaders__save-btn"
                onClick={handleSaveTeamLeaders}
                disabled={savingTeamLeaders}
              >
                {savingTeamLeaders ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Past Members Tab
  const renderPastMembersTab = () => (
    <div className="py-5">
      {pastMembers?.length === 0 ? (
      <div className="text-center mt-4 text-muted">
        No past members
      </div>
    ) : (pastMembers.map((member, index) => (
        <div key={index} className="mb-2">
          {member.name}
        </div>
      ))
    )}
    </div>
  );

  return (
    <div className="mt-4 crm-editorg-form">
      <div className="row border border-white p-3 rounded g-0 crm-editorg-card">
        <div className="col-12">
          <div className="crm-editorg-header mb-3">
            <div className="crm-editorg-header-top">
              <img src={logoPreview} alt="Logo" width="120" />
              <h1 className="mb-0 crm-editorg-title">{isEditMode ? `${formData.name}` : "Create Organization"}</h1>
            </div>
            <div className="crm-editorg-upload-row mt-2">
              <button type="button" className="btn btn-secondary px-5 py-3 mt-2" onClick={() => document.getElementById("logoInput").click()}>
                Upload Logo
              </button>
              <input type="file" id="logoInput" style={{ display: "none" }} accept="image/*" onChange={handleLogoUpload} />
            </div>
          </div>
          <div className="tabs d-flex flex-wrap gap-2">
            {["Profile", "Address", ...(isEditMode ? ["Members", "Past Members"] : [])].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`btn px-4 py-3 ${activeTab === tab ? "btn-warning" : "btn-success"}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="crm-editorg-form-inner">
            {activeTab === "Profile" && renderProfileTab()}
            {activeTab === "Address" && renderAddressTab()}
            {activeTab === "Members" && renderMembersTab()}
            {activeTab === "Past Members" && renderPastMembersTab()}

            {(activeTab === "Profile" || activeTab === "Address") && (
              <div className="mt-4 d-flex flex-wrap align-items-center gap-2 crm-editorg-form-actions">
                <button type="submit" className="btn btn-success px-5 py-3" style={{ fontWeight: "600", minWidth: "150px" }}>
                  Save
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {showLosAngelesSection && (
        <div className="crm-editorg-la-section-outer">
          <div className="row border border-white p-3 rounded g-0 crm-editorg-card">
            <div className="col-12">
              <div className="crm-editorg-la-section">
                {renderLosAngelesSection()}
              </div>
            </div>
          </div>
        </div>
      )}

      {dialog.isVisible && (
        <div className="dialog-overlay">
          <div className={`dialog-box ${dialog.type}`}>
            <button type="button" className="dialog-close-button" onClick={handleCloseDialog}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h4 className={`heading ${dialog.type}`}>{dialog.type}</h4>
            <p>{dialog.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEditOrganizationForm;
